/**
 * Operations Managers Page
 * Manufacturer-only management of operations manager accounts
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { operationManagerService, OperationManager } from '../services/operation-manager.service';
import {
  Alert,
  Button,
  FilterBar,
  Input,
  Modal,
  ModalFooter,
  PageHeader,
  StatusBadge
} from '../components/ui';

interface OperationManagerForm {
  fullName: string;
  email: string;
  phone: string;
}

export default function OperationManagersPage() {
  const navigate = useNavigate();
  const [managers, setManagers] = useState<OperationManager[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [tempPassword, setTempPassword] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [resettingId, setResettingId] = useState<string | null>(null);
  const [form, setForm] = useState<OperationManagerForm>({
    fullName: '',
    email: '',
    phone: '',
  });

  useEffect(() => {
    loadManagers();
  }, []);

  const loadManagers = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await operationManagerService.list();
      if (result.success && result.data) {
        setManagers(result.data);
      } else {
        setError(result.error || 'Failed to load operations managers');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load operations managers');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    try {
      setSubmitting(true);
      setError(null);
      setSuccess(null);

      if (!form.fullName.trim() || !form.email.trim()) {
        setError('Full name and email are required');
        return;
      }

      const result = await operationManagerService.create({
        fullName: form.fullName.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || undefined,
      });

      if (!result.success) {
        setError(result.error || 'Failed to create operations manager');
        return;
      }

      setShowAddModal(false);
      setForm({ fullName: '', email: '', phone: '' });
      await loadManagers();

      if (result.tempPassword) {
        setTempPassword(result.tempPassword);
        setShowPasswordModal(true);
        setSuccess(
          result.emailSent
            ? 'Operations manager created. Invite email sent.'
            : 'Operations manager created. Invite email could not be sent.'
        );
      } else {
        setSuccess('Operations manager created successfully.');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create operations manager');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleStatus = async (manager: OperationManager) => {
    try {
      setError(null);
      const newStatus = manager.status === 'active' ? 'inactive' : 'active';
      const result = await operationManagerService.updateStatus(manager.id, newStatus);
      if (!result.success) {
        setError(result.error || 'Failed to update status');
        return;
      }
      await loadManagers();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update status');
    }
  };

  const handleResetPassword = async (manager: OperationManager) => {
    const confirmReset = confirm(`Reset password for ${manager.user.fullName}?`);
    if (!confirmReset) {
      return;
    }

    try {
      setResettingId(manager.id);
      setError(null);
      setSuccess(null);
      const result = await operationManagerService.resetPassword(manager.id);
      if (!result.success) {
        setError(result.error || 'Failed to reset password');
        return;
      }

      if (result.tempPassword) {
        setTempPassword(result.tempPassword);
        setShowPasswordModal(true);
      }

      setSuccess(
        result.emailSent
          ? 'Password reset email sent.'
          : 'Password reset. Email could not be sent.'
      );
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to reset password');
    } finally {
      setResettingId(null);
    }
  };

  const filteredManagers = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    return managers.filter((manager) => {
      const { user } = manager;
      const matchesSearch =
        !query ||
        user.fullName.toLowerCase().includes(query) ||
        user.email.toLowerCase().includes(query) ||
        (user.phone || '').toLowerCase().includes(query);

      const matchesStatus =
        statusFilter === 'all' ||
        (statusFilter === 'active' ? manager.status === 'active' : manager.status === 'inactive');

      return matchesSearch && matchesStatus;
    });
  }, [managers, searchTerm, statusFilter]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading operations managers...</p>
        </div>
      </div>
    );
  }

  const activeManagers = managers.filter((manager) => manager.status === 'active').length;
  const inactiveManagers = managers.length - activeManagers;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
      <PageHeader
        title="Operations Managers"
        description="Create, activate, and manage operations access for your team."
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" onClick={() => navigate('/settings')}>
              Back to Settings
            </Button>
            <Button onClick={() => setShowAddModal(true)} className="flex items-center gap-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add Operations Manager
            </Button>
          </div>
        }
      />

        {error && (
          <Alert variant="error" className="mb-6">
            {error}
          </Alert>
        )}
        {success && (
          <Alert variant="success" className="mb-6">
            {success}
          </Alert>
        )}

      <FilterBar
        searchValue={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder="Search by name, email, or phone..."
        statusOptions={[
          { value: 'all', label: 'All', count: managers.length },
          { value: 'active', label: 'Active', count: activeManagers },
          { value: 'inactive', label: 'Inactive', count: inactiveManagers },
        ]}
        selectedStatus={statusFilter}
        onStatusChange={(value) => setStatusFilter(value as 'all' | 'active' | 'inactive')}
      />

        {filteredManagers.length === 0 ? (
          <div className="rounded-xl border border-gray-200 bg-white p-12 text-center shadow-sm">
            <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Operations Managers</h3>
            <p className="text-gray-600 mb-4">Invite operations managers to help with batch creation and product updates.</p>
            <Button onClick={() => setShowAddModal(true)}>Add Operations Manager</Button>
          </div>
        ) : (
          <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                Team Members ({filteredManagers.length})
              </h2>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200" aria-label="Operations managers list">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Manager</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredManagers.map((manager) => (
                    <tr key={manager.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">{manager.user.fullName}</div>
                        <div className="text-xs text-gray-500">Added by {manager.createdBy.fullName}</div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700">
                        <div>{manager.user.email}</div>
                        {manager.user.phone && <div className="text-xs text-gray-500">{manager.user.phone}</div>}
                      </td>
                      <td className="px-6 py-4">
                        <StatusBadge tone={manager.status === 'active' ? 'active' : 'disabled'} size="sm">
                          {manager.status === 'active' ? 'Active' : 'Inactive'}
                        </StatusBadge>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {formatDate(manager.createdAt)}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="inline-flex items-center gap-4">
                          <button
                            onClick={() => handleResetPassword(manager)}
                            className="text-sm font-medium text-blue-600 hover:text-blue-900"
                            disabled={resettingId === manager.id}
                          >
                            {resettingId === manager.id ? 'Resetting...' : 'Reset Password'}
                          </button>
                          <button
                            onClick={() => handleToggleStatus(manager)}
                            className={`text-sm font-medium ${
                              manager.status === 'active' ? 'text-red-600 hover:text-red-900' : 'text-emerald-600 hover:text-emerald-900'
                            }`}
                          >
                            {manager.status === 'active' ? 'Deactivate' : 'Activate'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

      <Modal isOpen={showAddModal} onClose={() => setShowAddModal(false)} title="Add Operations Manager">
        <div className="space-y-4">
          <Input
            label="Full Name"
            value={form.fullName}
            onChange={(e) => setForm((prev) => ({ ...prev, fullName: e.target.value }))}
            required
          />
          <Input
            label="Email"
            type="email"
            value={form.email}
            onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))}
            required
          />
          <Input
            label="Phone (optional)"
            value={form.phone}
            onChange={(e) => setForm((prev) => ({ ...prev, phone: e.target.value }))}
          />
        </div>
        <ModalFooter>
          <Button variant="secondary" onClick={() => setShowAddModal(false)}>
            Cancel
          </Button>
          <Button onClick={handleCreate} disabled={submitting}>
            {submitting ? 'Creating...' : 'Create Manager'}
          </Button>
        </ModalFooter>
      </Modal>

      <Modal
        isOpen={showPasswordModal}
        onClose={() => setShowPasswordModal(false)}
        title="Temporary Password"
        size="sm"
      >
        <div className="space-y-3">
          <p className="text-sm text-gray-600">
            Share this temporary password with the operations manager. They should change it after first login.
          </p>
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 font-mono text-sm text-gray-900">
            {tempPassword}
          </div>
        </div>
        <ModalFooter>
          <Button onClick={() => setShowPasswordModal(false)}>Done</Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}
